<script lang="ts" setup>
import { getProjectDetails, type GetProjectDetailsProjectResponse } from '@/api';
import ProjectCarousel from '@/views/projects/ProjectCarousel.vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import ProjectModels from '@/views/projects/ProjectModels.vue';
import ProjectCharacteristics from '@/views/projects/ProjectCharacteristics.vue';
import ProjectStats from '@/views/projects/ProjectStats.vue';
import ProjectAnchors from '@/views/projects/ProjectAnchors.vue';
import ProjectActionBar from '@/views/projects/ProjectActionBar.vue';
import ProjectModelDialog from '@/views/projects/ProjectModelDialog.vue';
import NearbyProjects from '@/views/projects/NearbyProjects.vue';
import BuilderProjects from '@/views/projects/BuilderProjects.vue';
import ProjectBreadcrumb from '@/views/projects/ProjectBreadcrumb.vue';
import ProjectContact from '@/views/projects/ProjectContact.vue';
import ProjectBuilder from '@/views/projects/ProjectBuilder.vue';
import ProjectHeader from '@/views/projects/ProjectHeader.vue';
import ProjectDescription from '@/views/projects/ProjectDescription.vue';
import ProjectArticles from '@/views/projects/ProjectArticles.vue';
import ProjectMap from '@/views/projects/ProjectMap.vue';
import ContactForm from '@/components/ContactForm.vue';
import ProjectBadge from '@/components/ProjectBadge.vue';
import WebsiteButton from '@/components/WebsiteButton.vue';
import { useHead, type ResolvableLink } from '@unhead/vue';
import { useRoute, useRouter } from 'vue-router';
import { getCanonicalUrl, startingPrice } from '@/utilities';
import { useI18n } from 'vue-i18n';
import ProjectShare from '@/views/projects/ProjectShare.vue';
import { useTracking } from '@/utilities/tracking.ts';
import { useProjectJsonSchema } from '@/utilities/json-ld.ts';
import type { ProjectCarouselImage } from '@/views/projects/types.ts';
import { Constants } from '@/utilities/constants.ts';
import { buildHreflangLinks } from '@/utilities/seo.ts';
import { getAlternateLink } from '@/plugins/router/localized.ts';
import { useSsgState } from '@/utilities/ssg-state.ts';

const props = defineProps<{
  projectId: number;
}>();

const { trackPageView } = useTracking();
const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const canonicalUrl = computed(() => getCanonicalUrl(route.path));

const project = ref<GetProjectDetailsProjectResponse | null>(null);
const modelId = ref<number>(0);

const { generateProjectJsonSchema } = useProjectJsonSchema();
const projectJsonLd = ref<string | null>(null);

const loadProject = async () => {
  const result = await getProjectDetails({
    path: {
      projectId: props.projectId,
    },
  });

  if (!result.data) {
    throw new Error(`Failed to fetch project. ${JSON.stringify(result.error)}`);
  }

  if (project.value?.id !== result.data.id) {
    modelId.value = 0;
  }
  project.value = result.data;
  projectJsonLd.value = generateProjectJsonSchema(result.data);
};

const { ready } = useSsgState({
  key: `project:${props.projectId}`,
  refs: { project, projectJsonLd },
  isLoaded: () => project.value !== null,
  load: loadProject,
  refreshOnClient: true,
});

watch(
  () => props.projectId,
  () => {
    void loadProject();
    void trackPageView('project', props.projectId);
  },
  { immediate: false }
);

const primaryPhoneNumber = computed(() => {
  const p = project.value;
  if (!p || !p.isPaid) return null;

  const contacts = p.contacts ?? [];
  if (contacts.length > 0) {
    const contact = contacts[0];
    if (!contact) return null;
    if (contact.telephone) return contact.telephone;
    if (contact.cellular) return contact.cellular;
  }
  return null;
});

const barStartingPrice = computed(() => {
  const p = project.value;
  if (!p || !p.isPaid) return null;

  const price = startingPrice(p.models);
  return price ? price.amount.formatCurrency(locale.value) : null;
});

const carouselImages = computed<ProjectCarouselImage[]>(() => {
  const p = project.value;
  if (!p) return [];

  // Certains projets réutilisent les mêmes photos pour tous leurs modèles : la galerie
  // n'affiche chaque image qu'une seule fois, sous sa première section rencontrée.
  const images: ProjectCarouselImage[] = [];
  const seenSources = new Set<string>();
  const addImage = (image: ProjectCarouselImage) => {
    if (seenSources.has(image.src)) return;
    seenSources.add(image.src);
    images.push(image);
  };

  const projectSection = t('views.project.carousel.project');
  p.images.forEach((image) => addImage({ src: image.src, title: image.title, section: projectSection }));

  p.models.forEach((model) => {
    model.images.forEach((image) => {
      addImage({ src: image.src, title: image.title, section: model.name });
    });
  });

  return images;
});

const modelIdToSlug = computed(() => {
  if (!project.value) return {} as Record<number, string>;
  return Object.fromEntries(project.value.models.map((m) => [m.id, m.name.toSlug()])) as Record<number, string>;
});

function trySelectFromHash() {
  const hash = router.currentRoute.value.hash;
  const match = hash.match(/^#([^#]+)-(\d+)$/);
  if (!match) return null;
  const matchedId = Number(match[2]);
  const slug = modelIdToSlug.value[matchedId];
  if (slug) void updateModel(matchedId);
}

// Le dialogue est démonté sans transition, donc Headless UI ne restaure pas le focus.
// On le ramène nous-mêmes sur la carte d'où le visiteur est parti.
const elementBeforeDialog = ref<HTMLElement | null>(null);

const updateModel = async (id: number) => {
  if (id !== modelId.value) {
    if (id > 0 && modelId.value === 0) {
      elementBeforeDialog.value = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    } else if (id === 0) {
      const previous = elementBeforeDialog.value;
      elementBeforeDialog.value = null;
      if (previous?.isConnected) {
        setTimeout(() => previous.focus(), 0);
      }
    }

    modelId.value = id;
    await trackPageView(id > 0 ? 'projectModel' : 'project', id > 0 ? id : props.projectId);
  }
  const slug = modelIdToSlug.value[id];
  const newHash = slug ? `#${slug}-${id}` : '';
  if (router.currentRoute.value.hash !== `${newHash}`) {
    await router.replace({ hash: newHash ? `${newHash}` : '' });
  }
};

// La barre d'action mobile ramène le visiteur au formulaire de la page ; le popup
// de modèle possède maintenant son propre ContactForm avec des identifiants uniques.
const contactAnchorId = 'formulaire-contact';

// La barre d'ancres ne liste que les sections réellement rendues, ce qui la vide
// naturellement sur les projets sans modèle.
const anchorSections = computed(() => {
  const p = project.value;
  if (!p) return [];

  const sections: { id: string; label: string }[] = [{ id: 'infos-generales', label: t('views.project.anchors.overview') }];
  if (p.models.length > 0) sections.push({ id: 'modeles', label: t('views.project.anchors.models') });
  if (p.tags.length > 0 || p.description.length > 0) sections.push({ id: p.tags.length > 0 ? 'caracteristiques' : 'description', label: t('views.project.anchors.details') });
  sections.push({ id: 'emplacement', label: t('views.project.anchors.map') });
  sections.push({ id: 'constructeur', label: t('views.project.anchors.builder') });
  return sections;
});

const openContactForm = async () => {
  await updateModel(0);
  await nextTick();
  // La fermeture du dialogue remet la position de défilement à zéro pendant l'image d'animation
  // en cours : un requestAnimationFrame arrive trop tôt et se fait écraser, alors qu'une tâche
  // différée passe après. Le mode de défilement reste celui du navigateur, ce qui respecte les
  // préférences système.
  setTimeout(() => {
    document.getElementById(contactAnchorId)?.scrollIntoView({ block: 'start' });
  }, 0);
};

onMounted(async () => {
  await ready();
  trySelectFromHash();
  await trackPageView('project', props.projectId);
});

watch(
  () => router.currentRoute.value.hash,
  () => {
    trySelectFromHash();
  }
);

/* ---------------------- HEAD META ---------------------- */

const alternateLink = computed(() => getAlternateLink(route) ?? null);

// Montréal et Laval sont les deux seules régions du Québec qui sont aussi des villes. Là-bas, la
// « ville » du projet est en réalité un secteur (Ville Marie, Rosemont, Île des Sœurs) que personne
// ne cherche : c'est la région qui porte le mot-clé. Partout ailleurs, la ville reste la bonne.
const titleLocation = computed(() => {
  const address = project.value?.address;
  if (!address) return '';

  const isCityRegion = address.region.id === Constants.RegionId.Montreal || address.region.id === Constants.RegionId.Laval;

  return isCityRegion ? address.region.name : address.city.name;
});

// L'étape du lieu dans le fil d'Ariane mène à la recherche filtrée correspondante :
// la région pour Montréal et Laval, la ville partout ailleurs, comme le libellé affiché.
const titleLocationRoute = computed(() => {
  const address = project.value?.address;
  if (!address) return null;

  const isCityRegion = address.region.id === Constants.RegionId.Montreal || address.region.id === Constants.RegionId.Laval;

  return isCityRegion ? { name: 'search', query: { regionId: address.region.id } } : { name: 'search', query: { cityId: address.city.id } };
});

// Un projet sur huit porte déjà son lieu dans son nom (« La Prairie sur le Lac »). Le répéter allonge
// le titre sans rien apporter. La comparaison passe par les slugs, qui ignorent la casse et les
// accents, et porte sur des mots entiers pour ne pas confondre « Gore » avec « Gorewood ».
const titleRepeatsLocation = computed(() => {
  const name = project.value?.name;
  const location = titleLocation.value;
  if (!name || !location) return false;

  return `-${name.toSlug()}-`.includes(`-${location.toSlug()}-`);
});

// Le type de propriété se répète souvent avec le nom du projet (« Florence Condos | Condos neufs »),
// et c'est voulu : le retirer ferait disparaître « neufs », le différenciateur du site.
// Un projet peut appartenir à plusieurs familles à la fois, et c'est la plus représentée qui l'emporte.
// Les sous-catégories comptent avec leur racine : « La Prairie sur le Lac » est maison, maison jumelée,
// maison de ville et condo, donc trois fois maison contre une fois condo. Les identifiants arrivent
// triés, alors le plus petit tranche les égalités.
const propertyType = computed(() => {
  const counts = new Map<string, number>();

  for (const id of project.value?.globalCategoryIds ?? []) {
    const key = Constants.ProjectTitlePropertyTypes[id];
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  let mostCommon = '';
  for (const [key, count] of counts) {
    if (count > (counts.get(mostCommon) ?? 0)) mostCommon = key;
  }

  return mostCommon ? t(`views.project.property-types.${mostCommon}`) : '';
});

// Le type de prix vient des catégorisations autant que des modèles, car plus de la moitié des projets
// n'ont aucun modèle. Les rares projets à la fois en vente et en location affichent « à vendre ».
const priceType = computed(() => {
  const priceTypeIds = project.value?.priceTypeIds ?? [];

  if (priceTypeIds.includes(Constants.PriceTypeId.ForSale)) return t('views.project.price-types.for-sale');
  if (priceTypeIds.includes(Constants.PriceTypeId.ForRent)) return t('views.project.price-types.for-rent');

  return '';
});

// Le suffixe « Projet Habitation » est retiré de ces titres : avec lui, 94 % dépassaient la coupure
// de Google, qui tombait alors au milieu du nom du site. Il ne sert plus que de repli pour un projet
// sans catégorie ni type de prix, dont le titre se réduirait autrement à son seul nom.
const pageTitle = computed(() => {
  const p = project.value;
  if (!p) return '';

  if (!propertyType.value || !priceType.value) return t('views.project.title', { header: p.name });

  const values = { name: p.name, propertyType: propertyType.value, priceType: priceType.value, location: titleLocation.value };

  return titleLocation.value && !titleRepeatsLocation.value ? t('views.project.meta.title-in-location', values) : t('views.project.meta.title', values);
});

useHead({
  title: () => pageTitle.value,
  meta: () => {
    const desc = project.value?.metaDescription.toPlainText() ?? '';
    return [
      { name: 'description', content: desc },
      { property: 'og:title', content: pageTitle.value },
      { property: 'og:description', content: desc },
      { property: 'og:url', content: canonicalUrl.value },
      { name: 'twitter:title', content: pageTitle.value },
      { name: 'twitter:description', content: desc },
      { name: 'twitter:card', content: 'summary' },
    ];
  },
  link: (): ResolvableLink[] => buildHreflangLinks(canonicalUrl.value, alternateLink.value),
  script: () =>
    projectJsonLd.value
      ? [
          {
            type: 'application/ld+json',
            innerHTML: projectJsonLd.value,
          },
        ]
      : [],
});
</script>

<template>
  <!-- La fiche projet déborde du conteneur commun : 85 % de la fenêtre pour toute la page, galerie
       comprise. Le max() garde la largeur héritée tant qu'elle est la plus grande, et le min() annule
       le décalage dans ce cas, si bien que rien ne bouge en deçà d'environ 1440 px. -->
  <article v-if="project" class="ml-[min(0px,calc(50%_-_42.5vw))] w-[max(100%,85vw)] max-w-none space-y-8 pb-24 md:space-y-12">
    <nav class="no-print mb-4 text-sm text-gray-500">
      <ol class="flex items-center space-x-2">
        <li>
          <RouterLink :to="{ name: 'home' }" class="hover:text-ph-green-600">{{ t('views.project.breadcrumbs.home') }}</RouterLink>
        </li>
        <li>/</li>
        <template v-if="titleLocation && titleLocationRoute">
          <li>
            <RouterLink :to="titleLocationRoute" class="hover:text-ph-green-600">{{ titleLocation }}</RouterLink>
          </li>
          <li>/</li>
        </template>
        <li class="text-gray-900">{{ project.name }}</li>
      </ol>
    </nav>
    <ProjectBreadcrumb class="no-print mb-4 text-sm" />
    <ProjectCarousel :images="carouselImages" :keyboardEnabled="modelId === 0">
      <ProjectBadge :banners="project.banners" :horizontal="true" :isNew="project.isNew" :isPaid="project.isPaid" :isSold="project.isSold" />
    </ProjectCarousel>
    <!-- Sous la galerie, la page se scinde en deux : le projet à gauche, le contact à droite.
         Sur une seule colonne, la même grille replace simplement le formulaire après la description. -->
    <div class="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_22rem] lg:gap-10 xl:grid-cols-[minmax(0,1fr)_24rem]">
      <div class="space-y-8 md:space-y-12">
        <!-- L'en-tête et les statistiques forment la section d'ouverture visée par la première ancre.
             Le conteneur reprend l'espacement vertical de la colonne, qu'il interrompt autrement. -->
        <div id="infos-generales" class="scroll-mt-20 space-y-8 md:space-y-12">
          <div class="flex justify-between">
            <ProjectHeader :project="project" />
            <ProjectShare :modelId="modelId" :projectId="project.id" />
          </div>
          <ProjectStats :project="project" />
        </div>
        <ProjectAnchors :sections="anchorSections" />
        <div id="modeles" class="scroll-mt-20">
          <ProjectModels :project="project" @click="updateModel" />
        </div>
        <div id="caracteristiques" class="scroll-mt-20">
          <ProjectCharacteristics :project="project" />
        </div>
        <div id="description" class="scroll-mt-20">
          <ProjectDescription :project="project" />
        </div>
        <ProjectArticles :project="project" />
      </div>

      <!-- Le décalage laisse passer la barre d'ancres, elle aussi collée en haut. -->
      <div :id="contactAnchorId" class="scroll-mt-20 lg:sticky lg:top-16">
        <!-- La continuité entre la barre d'action et le formulaire passe par le bouton, qui porte
             les mêmes mots des deux côtés. Le titre annonce plutôt ce que le visiteur y gagne. -->
        <ContactForm :header="t('views.project.contact.form-header')" :project="project" />
        <p v-if="project.isPaid && project.website" class="no-print mt-3 text-center">
          <WebsiteButton :label="t('views.project.contact.website-link')" :target-id="project.id" :url="project.website" target="project" variant="link" />
        </p>
      </div>
    </div>
    <!-- L'encadré Projet et la carte se partagent la largeur moitié-moitié dès que l'écran le permet. -->
    <div class="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-2">
      <ProjectContact :project="project" />
      <div id="emplacement" class="scroll-mt-20">
        <h2 class="2xs:text-lg xs:text-xl mb-2 text-base font-semibold">{{ t('views.project.anchors.map') }}</h2>
        <ProjectMap :project="project" class="lg:h-[calc(100%-2.25rem)]" />
      </div>
    </div>
    <BuilderProjects :project="project" />
    <div id="constructeur" class="scroll-mt-20">
      <ProjectBuilder :builder="project.builder" />
    </div>
    <NearbyProjects :project="project" />

    <ProjectModelDialog :modelId="modelId" :phoneNumber="primaryPhoneNumber" :project="project" @close="updateModel(0)" />
    <ProjectActionBar :formAnchorId="contactAnchorId" :phoneNumber="primaryPhoneNumber" :projectId="project.id" :projectName="project.name" :startingPrice="barStartingPrice" @contact="openContactForm" />
  </article>
</template>

<style lang="css" scoped></style>
